import { useCallback, useEffect, useState } from 'react';
import {
  getOnboardingActivationStatus,
  startOnboardingActivation,
} from '../services/tenantService';

export function useOnboardingActivation(options = {}) {
  const {
    enabled = true,
    pollMs = 3000,
    maxRunningMs = 5 * 60 * 1000,
  } = options;

  const [activation, setActivation] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setActivation(null);
      setLoading(false);
      return null;
    }

    try {
      const response = await getOnboardingActivationStatus();
      const nextActivation = response?.data || null;
      setActivation(nextActivation);
      setError(null);
      return nextActivation;
    } catch (err) {
      setError(err.message || 'Failed to load automatic setup status');
      return null;
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const activationTimedOut = Boolean(
    activation?.state === 'running'
    && activation?.startedAt
    && (Date.now() - new Date(activation.startedAt).getTime()) > maxRunningMs
  );

  useEffect(() => {
    setLoading(enabled);
    void refresh();
  }, [enabled, refresh]);

  useEffect(() => {
    if (!enabled || activation?.state !== 'running' || activationTimedOut) return undefined;

    const interval = window.setInterval(() => {
      void refresh();
    }, pollMs);

    return () => window.clearInterval(interval);
  }, [activation?.state, activationTimedOut, enabled, pollMs, refresh]);

  useEffect(() => {
    if (!activationTimedOut) return;

    setError('Automatic setup is taking longer than expected. You can keep working, then refresh this page in a moment.');
  }, [activationTimedOut]);

  const start = useCallback(async (payload = {}) => {
    if (!enabled) return null;

    setStarting(true);
    try {
      const response = await startOnboardingActivation(payload);
      const nextActivation = response?.data || null;
      if (nextActivation) {
        setActivation(nextActivation);
      }
      setError(null);
      return nextActivation;
    } catch (err) {
      setError(err.message || 'Failed to start automatic setup');
      throw err;
    } finally {
      setStarting(false);
    }
  }, [enabled]);

  return {
    activation,
    loading,
    starting,
    error,
    refresh,
    start,
  };
}

export default useOnboardingActivation;
